import cn from 'classnames';
import { useRouter } from 'next/router';
import type { ReactNode, SyntheticEvent } from 'react';
import React, { memo } from 'react';

import type { ButtonProps } from '@sravni/react-design-system/dist/types/components/Button';
import { Button } from '@sravni/react-design-system/lib/Button';

import type { AffSub4 } from '@src/@types/microcredits';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { MONETIZATION_INTERNAL } from '@src/helpers/advertising';
import type { IAnalyticsItem } from '@src/helpers/analyticsEvents';
import { sendAdvertisingButtonClickEvent } from '@src/helpers/analyticsEvents';
import { open } from '@src/helpers/navigationHelper';
import { usePixel } from '@src/hooks/usePixel';
import { isWhiteLabelDeniedPage } from '@src/utils/routing';
import { getPositionFromUrl } from 'widgets/LightweightCard/lib/getPositionFromUrl';

import styles from './styles.module.scss';

export interface AdvertisingButtonProps extends ButtonProps {
  className?: string;
  advSub?: string;
  source?: string;
  analytics?: IAnalyticsItem;
  visibleLabel?: ReactNode;
  productLinkHref?: string;
  affSub4?: AffSub4;
  link?: string;

  isCheckLink?: boolean;
  forceRedirectToFullDeal?: boolean;

  linkTarget?: '_self' | '_blank' | '_parent' | '_top';

  onClick?: (event: SyntheticEvent<Element, Event>) => void;
}

export const AdvertisingButton: React.FunctionComponent<AdvertisingButtonProps> = memo(
  ({
    className,
    source,
    analytics,
    advSub,
    affSub4,
    visibleLabel = 'Подобрать займ',
    link = analytics?.advertising?.monetization?.pixels?.click,
    linkTarget = '_blank',
    onClick,
    ...buttonProps
  }) => {
    const router = useRouter();
    const [pathname] = router.asPath.split('?');

    const isAvitoClient = router?.query?.aff_id === '12406' && isWhiteLabelDeniedPage(pathname);
    const sourcePosition = getPositionFromUrl(router);

    const monetizationKind = analytics?.advertising?.monetization?.kind;

    // todo(MFO-1414): временно (а может и нет) скрыли фейковый лэйбл
    // const buttonLabel = getButtonLabel({ isDetailsPage });

    const { createPixelLink } = usePixel(link, {
      sub: advSub,
      affSub4,
      source: sourcePosition || source,
      pixelType: PIXELS_TYPES.CLICK,
    });

    const handleAnalyticClick = (event: SyntheticEvent) => {
      event.stopPropagation();
      if (analytics) {
        sendAdvertisingButtonClickEvent(analytics);
      }
    };

    const handleClick = (event: SyntheticEvent) => {
      event.stopPropagation();
      if (analytics) {
        sendAdvertisingButtonClickEvent(analytics);
      }
      onClick?.(event);

      const href = createPixelLink();

      // @ts-ignore
      if (!href || analytics?._features?.isBankruptcyOffer) {
        return;
      }

      if (href.startsWith('https://sravni.go2cloud.org')) {
        // eslint-disable-next-line no-console
        console.log('XO_Error', href);
      }
      if (monetizationKind === MONETIZATION_INTERNAL) {
        router.push(href);
        return;
      }

      if (href) {
        open({
          href,
          target: linkTarget,
          features: 'noopener',
        });
      }
    };

    if (isAvitoClient) {
      const href = createPixelLink();
      return (
        <a
          className={cn(styles.link, className)}
          target="_self"
          rel="noopener"
          href={href}
          onClick={handleAnalyticClick}
        >
          <Button className={cn(styles.button, className)} variant="primary" {...buttonProps}>
            {visibleLabel}
          </Button>
        </a>
      );
    }

    // todo(MFO-1414): временно (а может и нет) скрыли фейковый лэйбл
    // const withFakeLabel = !isSpecial && !isDetailsPage;

    return (
      <Button className={cn(styles.button, className)} variant="primary" onClick={handleClick} {...buttonProps}>
        {visibleLabel}

        {/* <span className={styles.invisibleText}>Получить деньги</span> */}
        {/* todo(MFO-1414): временно (а может и нет) скрыли фейковый лэйбл */}
        {/* {withFakeLabel && (
                        <>

                            <span>{visibleLabel}</span>
                        </>
                    )}
                    {!withFakeLabel && } */}
      </Button>
    );
  },
);
